import React, { useState, useRef, useEffect } from 'react';
import labels from '../data/labels';

// Confetti animation component
const Confetti = () => {
  const containerRef = useRef(null);
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      const size = 5 + Math.random() * 10;
      const x = (Math.random() - 0.5) * 200;
      const y = (Math.random() - 0.5) * 200;
      const duration = 0.8 + Math.random() * 0.6;
      const colors = ['#FF5252', '#FFD740', '#64FFDA', '#40C4FF', '#E040FB', '#69F0AE'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      particle.style.position = 'absolute';
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.backgroundColor = color;
      particle.style.borderRadius = '50%';
      particle.style.left = '50%';
      particle.style.top = '50%';
      particle.style.opacity = '0';
      particle.style.transform = 'translate(-50%, -50%)';
      particle.animate([
        { transform: 'translate(-50%, -50%)', opacity: 0 },
        { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`, opacity: 0.8 },
        { transform: `translate(calc(-50% + ${x * 1.5}px), calc(-50% + ${y * 1.5}px))`, opacity: 0 }
      ], {
        duration: duration * 1000,
        easing: 'cubic-bezier(0.1, 0.8, 0.2, 1)',
        fill: 'forwards'
      });
      container.appendChild(particle);
    }
    return () => { if (container) container.innerHTML = ''; };
  }, []);
  return <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none z-10" />;
};

const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const MathGames = ({ language, onScore }) => {
  const t = labels[language] || labels['de'];
  // Step state
  const [step, setStep] = useState(0); // 0: choose game, 1: choose number (for multiplication) or directly generate equation (division/plusminus), 2: choose mode (for multiplication only), 3: play, 4: feedback, 5: end
  const [mode, setMode] = useState(null); // 'guess' or 'alone'
  const [selectedNumber, setSelectedNumber] = useState(null); // for multiplication only
  const [equation, setEquation] = useState(null); // {a, b, answer}
  const [choices, setChoices] = useState([]);
  const [userAnswer, setUserAnswer] = useState(null);
  const [result, setResult] = useState(null); // 'correct' | 'wrong'
  const [showConfetti, setShowConfetti] = useState(false);
  const [userLastAnswer, setUserLastAnswer] = useState(null);
  const [lastEquation, setLastEquation] = useState(null);
  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [gameType, setGameType] = useState(null); // 'multiplication', 'division', 'plusminus'
  const [previousEquations, setPreviousEquations] = useState([]); // Track previous equations to avoid repeats
  
  // Ref to track if we've saved the score already
  const scoreHasBeenSaved = useRef(false);
  
  // Save score to localStorage when game is complete (step 5)
  useEffect(() => {
    if (step === 5 && !scoreHasBeenSaved.current) {
      scoreHasBeenSaved.current = true; // Mark as saved to prevent duplicate saves
      
      try {
        // Get current user profile
        const profile = JSON.parse(localStorage.getItem("blitzlesenUser") || "{}");
        
        if (profile && profile.username) {
          // Initialize scores object if it doesn't exist
          if (!profile.scores) {
            profile.scores = {};
          }
          
          // Initialize array for this game type if it doesn't exist
          if (!profile.scores[gameType]) {
            profile.scores[gameType] = [];
          }
          
          // Add the new score
          profile.scores[gameType].push({
            score,
            timestamp: new Date().toISOString()
          });
          
          // Save updated profile back to localStorage
          localStorage.setItem("blitzlesenUser", JSON.stringify(profile));
          
          // Call onScore if provided (for parent components)
          if (onScore) {
            onScore(gameType, score);
          }
        }
      } catch (error) {
        console.error("Error saving score:", error);
      }
    }
    
    // Reset the ref when we leave step 5
    if (step !== 5) {
      scoreHasBeenSaved.current = false;
    }
  }, [step, gameType, score, onScore]);
  
  // Reset previous equations when game ends
  useEffect(() => {
    if (step === 5) {
      // When the game ends, we'll clear the previous equations next time
      // This ensures questions can repeat in a new game session
      const resetPreviousEquations = () => {
        setPreviousEquations([]);
      };
      
      // Schedule the reset for after the current render
      const timeoutId = setTimeout(resetPreviousEquations, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [step]);
  
  // Helper function to generate a non-repeating random equation - BUT doesn't update state
  const generateUniqueEquation = (type, prevEquations, aValue = selectedNumber) => {
    let eq;
    let isUnique = false;
    let attempts = 0;
    const maxAttempts = 20; // Avoid infinite loops
    
    while (!isUnique && attempts < maxAttempts) {
      attempts++;
      
      if (type === 'multiplication') {
        // For multiplication, use the provided aValue but with a different second number
        const a = aValue;
        let b;
        // Find a 'b' value that hasn't been used yet for this a
        const usedBValues = prevEquations
          .filter(prevEq => prevEq.a === a)
          .map(prevEq => prevEq.b);
        // If we've used all possible values, just pick a random one
        if (usedBValues.length >= 10) {
          b = getRandomInt(1, 10);
        } else {
          do {
            b = getRandomInt(1, 10);
          } while (usedBValues.includes(b));
        }
        eq = { a, b, answer: a * b };
      } else if (type === 'division') {
        // For division, generate division problems
        // First pick a divisor between 1-10
        const divisor = getRandomInt(1, 10);
        // Then pick a multiplier between 1-3 to get a dividend <= 30
        const multiplier = getRandomInt(1, 3);
        // Calculate dividend
        const dividend = divisor * multiplier;
        
        eq = { a: dividend, b: divisor, answer: multiplier };
      } else if (type === 'tripleplusminus') {
        // Generate a, b, c and two operators, ensuring no negative results at any step
        const a = getRandomInt(1, 30);
        let op1 = Math.random() > 0.5 ? '+' : '-';
        let b;
        if (op1 === '+') {
          b = getRandomInt(1, 30 - a);
        } else {
          b = getRandomInt(0, a);
        }
        let op2 = Math.random() > 0.5 ? '+' : '-';
        let c;
        let intermediate;
        if (op1 === '+') {
          intermediate = a + b;
        } else {
          intermediate = a - b;
        }
        if (op2 === '+') {
          c = getRandomInt(1, 30 - intermediate);
        } else {
          c = getRandomInt(0, intermediate);
        }
        // Calculate answer
        let answer;
        if (op1 === '+') {
          answer = a + b;
        } else {
          answer = a - b;
        }
        if (op2 === '+') {
          answer = answer + c;
        } else {
          answer = answer - c;
        }
        eq = { a, b, c, op1, op2, answer };
      } else { // plusminus
        const isAddition = Math.random() > 0.5;
        if (isAddition) {
          const a = getRandomInt(1, 20);
          const b = getRandomInt(1, 30 - a);
          eq = { a, b, operator: '+', answer: a + b };
        } else {
          const a = getRandomInt(1, 30);
          const b = getRandomInt(0, a);
          eq = { a, b, operator: '-', answer: a - b };
        }
      }
      
      // Check if this equation already appeared in this game session
      isUnique = !prevEquations.some(prevEq => {
        if (type === 'multiplication') {
          return prevEq.a === eq.a && prevEq.b === eq.b;
        } else if (type === 'division') {
          return prevEq.a === eq.a && prevEq.b === eq.b;
        } else if (type === 'tripleplusminus') {
          return prevEq.a === eq.a && prevEq.b === eq.b && prevEq.c === eq.c && prevEq.op1 === eq.op1 && prevEq.op2 === eq.op2;
        } else { // plusminus
          return prevEq.a === eq.a && prevEq.b === eq.b && prevEq.operator === eq.operator;
        }
      });
    }
    
    return eq;
  };

  // Step 0: Choose game type
  if (step === 0) {
    return (
      <div className="p-4 max-w-md mx-auto bg-blue-100 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold text-center mb-4">{t.math || 'Mathe'}</h1>
        <div className="flex flex-col gap-4">
          <button className="green-button" onClick={() => { 
            setGameType('multiplication'); 
            setStep(1); 
            setMode(null); 
            setSelectedNumber(null); 
            setEquation(null); 
            setChoices([]); 
            setUserAnswer(null); 
            setResult(null); 
            setShowConfetti(false); 
            setUserLastAnswer(null); 
            setLastEquation(null); 
            setRound(1); 
            setScore(0); 
            setPreviousEquations([]); // Reset previous equations
          }}>{t.multiplication}</button>
          <button className="green-button" onClick={() => { 
            setGameType('division'); 
            setStep(1); 
            setMode(null); 
            setSelectedNumber(null); 
            setEquation(null); 
            setChoices([]); 
            setUserAnswer(null); 
            setResult(null); 
            setShowConfetti(false); 
            setUserLastAnswer(null); 
            setLastEquation(null); 
            setRound(1); 
            setScore(0); 
            setPreviousEquations([]); // Reset previous equations
          }}>{t.division}</button>
          <button className="green-button" onClick={() => { 
            setGameType('plusminus'); 
            setStep(1); 
            setMode(null); 
            setSelectedNumber(null); 
            setEquation(null); 
            setChoices([]); 
            setUserAnswer(null); 
            setResult(null); 
            setShowConfetti(false); 
            setUserLastAnswer(null); 
            setLastEquation(null); 
            setRound(1); 
            setScore(0); 
            setPreviousEquations([]); // Reset previous equations
          }}>{t.plusminus}</button>
          <button className="green-button" onClick={() => { 
            setGameType('tripleplusminus'); 
            setStep(1); 
            setMode(null); 
            setSelectedNumber(null); 
            setEquation(null); 
            setChoices([]); 
            setUserAnswer(null); 
            setResult(null); 
            setShowConfetti(false); 
            setUserLastAnswer(null); 
            setLastEquation(null); 
            setRound(1); 
            setScore(0); 
            setPreviousEquations([]); // Reset previous equations
          }}>{t.tripleplusminus}</button>
        </div>
      </div>
    );
  }

  // Step 1: For multiplication - choose number; For division/plusminus - generate random equation
  if (step === 1) {
    // For division and plusminus, generate equation and move to appropriate next step
    if (gameType === 'division' || gameType === 'plusminus' || gameType === 'tripleplusminus') {
      // Generate a unique equation
      const eq = generateUniqueEquation(gameType, previousEquations);
      
      // Update state with the new equation and add to previous equations
      setEquation(eq);
      setPreviousEquations(prev => [...prev, eq]);
      
      // Move to step 2 for mode selection
      setStep(2);
      return null;
    }
    
    // For multiplication - choose number
    return (
      <div className="p-4 max-w-md mx-auto bg-blue-100 rounded-lg shadow-lg text-center">
        <h1 className="text-2xl font-bold mb-4">{t.math || 'Mathe'}</h1>
        <p className="mb-2">{t.chooseNumber || 'Choose a number:'}</p>
        <div className="grid grid-cols-5 gap-2 mb-4 max-w-xs mx-auto">
          {Array.from({ length: 10 }, (_, i) => i + 1).map(num => (
            <button
              key={num}
              className="green-button"
              onClick={() => {
                // Store the chosen multiplier 'a'
                setSelectedNumber(num);
                // Build the first equation manually, pick a random b
                const b = getRandomInt(1, 10);
                const eq = { a: num, b, answer: num * b };
                // Save equation and mark it as used
                setEquation(eq);
                setPreviousEquations([eq]);
                setStep(2);
              }}
            >
              {num}
            </button>
          ))}
        </div>
        <button className="small-button" onClick={() => setStep(0)}>{t.backToGame || 'Back to Game'}</button>
      </div>
    );
  }
  
  // Step 2: Choose mode (for all game types)
  if (step === 2) {
    let equationDisplay;

    if (gameType === 'multiplication') {
      // Display the existing multiplication equation
      equationDisplay = `${equation.a} × ${equation.b} = ?`;
    } else if (gameType === 'division') {
      // Division equation is already generated in step 1
      equationDisplay = `${equation.a} ÷ ${equation.b} = ?`;
    } else if (gameType === 'tripleplusminus') {
      equationDisplay = `${equation.a} ${equation.op1} ${equation.b} ${equation.op2} ${equation.c} = ?`;
    } else { // plusminus
      // Plusminus equation is already generated in step 1
      if (equation?.operator === '+') {
        equationDisplay = `${equation.a} + ${equation.b} = ?`;
      } else {
        equationDisplay = `${equation.a} - ${equation.b} = ?`;
      }
    }

    return (
      <div className="p-4 max-w-md mx-auto bg-blue-100 rounded-lg shadow-lg text-center">
        <h1 className="text-2xl font-bold mb-4">{t.math || 'Mathe'}</h1>
        <p className="math-equation">{equationDisplay}</p>
        <p className="mb-2">{t.chooseGameMode || 'Wähle den Modus:'}</p>
        <div className="flex justify-center gap-4 mb-4">
          <button className="green-button" onClick={() => { 
            setMode('guess'); 
            
            // Generate choices
            let options = [equation.answer];
            // eslint-disable-next-line no-loop-func
            while (options.length < 3) {
              const offset = getRandomInt(1, 10);
              let wrong = equation.answer + (Math.random() > 0.5 ? offset : -offset);
              
              // Ensure valid answers
              if (!options.includes(wrong) && wrong >= 0) {
                // For plusminus and division, keep answers under 30
                if ((gameType === 'division' || gameType === 'plusminus' || gameType === 'tripleplusminus') && wrong <= 30) {
                  options.push(wrong);
                } 
                // For multiplication, allow higher numbers but keep it reasonable
                else if (gameType === 'multiplication' && wrong <= 100) {
                  options.push(wrong);
                }
              }
            }
            setChoices(options.sort(() => Math.random() - 0.5));
            
            setStep(3);
          }}>{t.guessMode || 'Raten (3 Möglichkeiten)'}</button>
          <button className="green-button" onClick={() => { 
            setMode('alone'); 
            setChoices([]);
            setStep(3);
          }}>{t.aloneMode || 'Allein (Antwort tippen)'}</button>
        </div>
        <button className="small-button" onClick={() => setStep(0)}>{t.backToGame || 'Back to Game'}</button>
      </div>
    );
  }

  // Previous Step 2 is now replaced by the updated code above

  // Step 3: Play round (for any game type)
  if (step === 3 && mode && equation) {
    // Determine the equation display based on game type
    let equationDisplay;
    if (gameType === 'multiplication') {
      equationDisplay = `${equation.a} × ${equation.b} = ?`;
    } else if (gameType === 'division') {
      equationDisplay = `${equation.a} ÷ ${equation.b} = ?`;
    } else if (gameType === 'tripleplusminus') {
      equationDisplay = `${equation.a} ${equation.op1} ${equation.b} ${equation.op2} ${equation.c} = ?`;
    } else { // plusminus
      equationDisplay = `${equation.a} ${equation.operator} ${equation.b} = ?`;
    }
    
    return (
      <div className="p-4 max-w-md mx-auto bg-blue-100 rounded-lg shadow-lg text-center relative">
        <h1 className="text-2xl font-bold mb-4">{t.math || 'Mathe'}</h1>
        <p className="math-equation">{equationDisplay}</p>
        {mode === 'guess' && (
          <div>
            <p className="mb-3">{t.pickCorrectAnswer || 'Wähle die richtige Antwort:'}</p>
            <div className="flex justify-center gap-4 mb-4">
              {choices.map(choice => (
                <button key={choice} className="green-button" onClick={() => {
                  setUserAnswer(choice);
                  setUserLastAnswer(choice);
                  setLastEquation(equation);
                  const isCorrect = choice === equation.answer; 
                  setResult(isCorrect ? 'correct' : 'wrong');
                  if (isCorrect) {
                    setScore(prevScore => prevScore + 1);
                  }
                  setShowConfetti(isCorrect);
                  setStep(4);
                }}>{choice}</button>
              ))}
            </div>
          </div>
        )}
        {mode === 'alone' && (
          <div>
            <input type="number" className="green-button" style={{ width: 120, fontSize: '1.5em' }} value={userAnswer === null ? '' : userAnswer} onChange={e => setUserAnswer(Number(e.target.value))} />
            <button className="green-button" onClick={() => {
              setUserLastAnswer(userAnswer);
              setLastEquation(equation);
              const isCorrect = userAnswer === equation.answer;
              setResult(isCorrect ? 'correct' : 'wrong');
              if (isCorrect) {
                setScore(prevScore => prevScore + 1);
              }
              setShowConfetti(isCorrect);
              setStep(4);
            }} disabled={userAnswer === null || userAnswer === ''}>{t.confirm || 'Bestätigen'}</button>
          </div>
        )}
        <button className="small-button mt-4" onClick={() => { setStep(0); setSelectedNumber(null); setEquation(null); setUserAnswer(null); setUserLastAnswer(null); setLastEquation(null); setResult(null); setShowConfetti(false); setChoices([]); }}>{t.backToGame || 'Back to Game'}</button>
      </div>
    );
  }

  // Step 4: Show feedback (for any game type)
  if (step === 4 && lastEquation) {
    // Determine equation display based on game type
    let equationDisplay, correctAnswerDisplay, wrongAnswerDisplay;
    
    if (gameType === 'multiplication') {
      equationDisplay = `${lastEquation.a} × ${lastEquation.b} = ?`;
      correctAnswerDisplay = `${lastEquation.a} × ${lastEquation.b} = ${lastEquation.answer}`;
      wrongAnswerDisplay = `${lastEquation.a} × ${lastEquation.b} = ${userLastAnswer} ❌`;
    } else if (gameType === 'division') {
      equationDisplay = `${lastEquation.a} ÷ ${lastEquation.b} = ?`;
      correctAnswerDisplay = `${lastEquation.a} ÷ ${lastEquation.b} = ${lastEquation.answer}`;
      wrongAnswerDisplay = `${lastEquation.a} ÷ ${lastEquation.b} = ${userLastAnswer} ❌`;
    } else if (gameType === 'tripleplusminus') {
      equationDisplay = `${lastEquation.a} ${lastEquation.op1} ${lastEquation.b} ${lastEquation.op2} ${lastEquation.c} = ?`;
      correctAnswerDisplay = `${lastEquation.a} ${lastEquation.op1} ${lastEquation.b} ${lastEquation.op2} ${lastEquation.c} = ${lastEquation.answer}`;
      wrongAnswerDisplay = `${lastEquation.a} ${lastEquation.op1} ${lastEquation.b} ${lastEquation.op2} ${lastEquation.c} = ${userLastAnswer} ❌`;
    } else { // plusminus
      equationDisplay = `${lastEquation.a} ${lastEquation.operator} ${lastEquation.b} = ?`;
      correctAnswerDisplay = `${lastEquation.a} ${lastEquation.operator} ${lastEquation.b} = ${lastEquation.answer}`;
      wrongAnswerDisplay = `${lastEquation.a} ${lastEquation.operator} ${lastEquation.b} = ${userLastAnswer} ❌`;
    }
    
    return (
      <div className="p-4 max-w-md mx-auto bg-blue-100 rounded-lg shadow-lg text-center relative">
        <h1 className="text-2xl font-bold mb-4">{t.math || 'Mathe'}</h1>
        <p className="math-equation">{equationDisplay}</p>
        {showConfetti && <Confetti />}
        {result === 'correct' && (
          <div className="p-4 rounded-lg mb-4 bg-green-100">
            <span style={{ fontWeight: 600 }}>{correctAnswerDisplay}</span>
            <div style={{ color: '#388e3c', fontWeight: 600, marginTop: 8 }}>{t.correct || 'Correct!'}</div>
          </div>
        )}
        {result === 'wrong' && (
          <div className="p-4 rounded-lg mb-4 bg-red-100">
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: '#d32f2f' }}>{wrongAnswerDisplay}</span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#388e3c' }}>{correctAnswerDisplay} ✅</span>
            </div>
            <div style={{ color: '#d32f2f', fontWeight: 600, marginTop: 8 }}>{t.wrong || 'Wrong!'}</div>
          </div>
        )}
        <button className="green-button" onClick={() => {
          // Next round or end
          if (round < 3) {
            // Reset state for next round
            setUserAnswer(null);
            setUserLastAnswer(null);
            setLastEquation(null);
            setResult(null);
            setShowConfetti(false);
            setRound(round + 1);
            
            // Handle each game type differently
            if (gameType === 'multiplication' || gameType === 'division' || gameType === 'tripleplusminus') {
              // Generate a new unique equation for the next round
              if (gameType === 'multiplication') {
                const eq = generateUniqueEquation('multiplication', previousEquations, equation.a);
                setEquation(eq);
                setPreviousEquations(prev => [...prev, eq]);
              } else if (gameType === 'division') {
                const eq = generateUniqueEquation('division', previousEquations);
                setEquation(eq);
                setPreviousEquations(prev => [...prev, eq]);
              } else { // tripleplusminus
                const eq = generateUniqueEquation('tripleplusminus', previousEquations);
                setEquation(eq);
                setPreviousEquations(prev => [...prev, eq]);
              }
              // Go back to mode selection for the next round
              setStep(2);
            } else { // plusminus
              // For plusminus: Generate new equation but keep the same mode
              const eq = generateUniqueEquation('plusminus', previousEquations);
              
              // Set the new equation
              setEquation(eq);
              setPreviousEquations(prev => [...prev, eq]);
              
              // If guess mode, generate new choices
              if (mode === 'guess') {
                let options = [eq.answer];
                while (options.length < 3) {
                  const offset = getRandomInt(1, 5);
                  let wrong = eq.answer + (Math.random() > 0.5 ? offset : -offset);
                  if (!options.includes(wrong) && wrong >= 0 && wrong <= 30) options.push(wrong);
                }
                setChoices(options.sort(() => Math.random() - 0.5));
              }
              
              // Go directly to play
              setStep(3);
            }
          } else {
            setStep(5);
          }
        }}>{round < 3 ? t.next || 'Weiter' : t.result || 'Result'}</button>
      </div>
    );
  }

  // Step 5: End of game (for any game type)
  if (step === 5) {
    const maxRounds = 3; // All games have 3 rounds
    
    return (
      <div className="p-4 max-w-md mx-auto bg-blue-100 rounded-lg shadow-lg text-center">
        <h1 className="text-2xl font-bold mb-4">{t.math || 'Mathe'}</h1>
        <h2 className="mb-4">{t.result || 'Result'}: {score} / {maxRounds}</h2>
        <button className="green-button" onClick={() => { setStep(0); setMode(null); setSelectedNumber(null); setEquation(null); setChoices([]); setUserAnswer(null); setResult(null); setShowConfetti(false); setUserLastAnswer(null); setLastEquation(null); setRound(1); setScore(0); }}>{t.playAgain || 'Nochmal spielen'}</button>
      </div>
    );
  }

  // Fallback (should not happen)
  return null;
};

export default MathGames; 